'use strict';

// Connect to the NGP VAN Databag, retrieve and set bag data, handle
// all denylisting, add/remove .Value to work with Oberon.
define('databag',['backbone', 'deviceInfo', 'databag_config', 'ActionTagConfig', 'cookieConsentConfiguration'],
function Databag(Backbone, deviceInfo, databag_config, actionTagConfig, cookieConsentConfig) {
  // Ensure this is on or IE will fail.
  Backbone.emulateHTTP = !!deviceInfo.ie;

  var _ = Backbone._;
  var $ = Backbone.$;

  var dotValue = {
    regex: /\.Value$/,
    value: '.Value'
  };

  var databag_root = actionTagConfig.DATABAG_ENDPOINT;
  if (databag_root && !/\/$/.test(databag_root)) {
    databag_root += '/';
  }

  var databagDomains =_.clone(actionTagConfig.DATABAG_SYNC_DOMAINS);

  var isDisclosureField = /^DisclosureFieldsForDonor_(\d+)$/;

  // Databag 2.0 Model
  return Backbone.Model.extend({
    __name__: 'Databag',
    databag_root: databag_root,
    internal_keys: ['id', 'name'],
    clear: function () {
      _(this.getDict()).keys().each(this.unset, this);
      this.trigger('clear');
      return this;
    },
    getDict: function () {
      var dict = _.omit(this.attributes, this.internal_keys);

      var fullName = this.name();
      if (fullName) {
        dict.FullName = fullName;
      }

      return dict;
    },
    set: function (key, val, options) {
      /*jshint -W116 */
      if (key == null) {
        /*jshint +W116 */
        return this;
      }
      var attrs, exclusions = this.denylist(), isValid = function (v, k) {
        return !_.isObject(v) && !_.contains(exclusions, k);
      };

      if (_.isObject(key)) {  // Handle `{ "key": value }` -style argument
        attrs = {};
        _.each(key, function (v, k) {
          if (isValid(v, k)) {
            attrs[k] = v;
          }
        }); //_.omit(key, exclusions); // Omit denylisted keys.
        options = val;
      } else if (isValid(val, key)) { // Handle `"key", value` -style argument if key isn't denylisted
        attrs = {};
        attrs[key] = val;
      } else { // We don't want to allow this key to be set if it's denylisted
        return this;
      }

      _.each(databag_config.canonical, function (key, canonical) {
        if (attrs.hasOwnProperty(canonical)) {
          attrs[key] = attrs[canonical];
          delete attrs[canonical];
        }
      });

      return Backbone.Model.prototype.set.call(this, attrs, options);
    },
    hardUnset: function (attr, options) { // jshint ignore:line
      // Can't use default Backbone unset behavior as it doesn't destroy key as well.
      // Default Backbone behavior is to just return undefined for value but keep
      // key around. We want it gone.
      delete this.attributes[attr];
      return this;
    },
    setWithFormState: function (form_state) {
      var state_copy;

      // Denylist values going into the Bag
      state_copy = _.omit(form_state, this.denylist());

      state_copy = _.reduce(state_copy, function (memo, value, key) {
        if (typeof value !== 'string') {
          value = JSON.stringify(value);
        }
        memo[key] = value;
        return memo;
      }, {});

      // Per [DIGITAL-1249], if AddressLine1 is changed, then always overwrite the
      // value of AddressLine2, even if its new value is blank.
      if (this.get('AddressLine1') !== state_copy.AddressLine1) {
        state_copy.AddressLine2 = form_state.AddressLine2 || '';
      }

      // Set silent because without it, the fill() method was eventually being called after an
      // event trigger on set(), which was replacing the entered form values with ones from a
      // bag before the second bag submission.
      this.set(state_copy, {silent: true});
      return this;
    },
    urlRoot: databag_root + 'v2/data/',
    url: function () {
      return this.urlRoot + this.get('id') + '/' + this.get('name');
    },
    initialize: function () {
      var self = this;
      _.bindAll(this, 'clear', 'syncAcrossDomains');
      this.deferred = Backbone.$.Deferred();
      this.on('sync', function () {
        this.deferred.resolve(self);
        this.synced = true;
      });

      if (this.has('cache')) {
        this.cache = this.get('cache');
        this.hardUnset('cache');
      }
    },
    promise: function () {
      return this.deferred.promise();
    },
    denylist: function (kind) {
      var list = databag_config.denylist.slice();

      if (_.isString(kind) && /^(pull|push)$/i.test(kind)) {
        list = list.concat(this.internal_keys).concat(databag_config.pushPullDenylist);
      }

      return list;
    },
    toJSON: function () {
      return this.pick(function (val, key) {
        return _.contains(databag_config.allowlist, key)
          || isDisclosureField.test(key);
      });
    },
    save: function () {
		  // Convert canonical property names to AT-friendly names
      _.each(databag_config.canonical, function (key, canonical) {
        if (this.attributes.hasOwnProperty(key)) {
          this.attributes[canonical] = this.attributes[key];
          delete this.attributes[key];
        }
      }, this);
      return Backbone.Model.prototype.save.apply(this, arguments);
    },
    fetch: function (options) {
      // Don't want to fetch a bag without an id set!
      if (this.attributes.id) {
        options = options || {};

        // Add a timeout of 2 seconds to the Databag
        options.timeout = options.timeout || 2000;

        if (deviceInfo.ie) {
          options.cache = false;
        } else {
          options.cache = 'cache' in options ? options.cache : ('cache' in this ? this.cache : true);
        }

        // Don't allow databags to mix
        this.clear();

        // Makes a GET call using the options to this.url()
        // and assigns the results to this.attributes
        if (cookieConsentConfig.areFunctionalCookiesAccepted()) {
          if (options.suppressCrossDomainSync) {
            return Backbone.Model.prototype.fetch.call(this, options);
          } else {
            return Backbone.Model.prototype.fetch.call(this, options)
              .then(this.syncAcrossDomains);
          }
        } else {
          this.deferred.reject('Functional Cookies are currently rejected');
          return this.promise();
        }

      } else {
        this.deferred.reject('Cannot fetch a bag without an id');
        return this.promise();
      }
    },
    syncAcrossDomains: function () {
      var databagId = this.attributes.id;
      var name = this.get('name');

      // this function makes a request for a databag id cookie from every OA domain
      // it might not work with drupal hosted forms using dev environments,
      // please add those domains specifically for testing
      name = name === 'nvtag' ? 'Profile' : name; // fix bad name

      databagDomains.forEach(function (domain) {
        // run async, nothing to do if it fails
        $.ajax({
          type: 'GET',
          url: domain + '/Databag/' + name + '/' + databagId,
          crossDomain: true,
          xhrFields: { withCredentials: true }
        });
      });
    },
    name: function () {
      var name = '';
      if (this.has('FirstName')) {
        name += this.get('FirstName');
      }
      if (this.has('MiddleName')) {
        if (name) {
          name += ' ';
        }
        name += this.get('MiddleName');
      }
      if (this.has('LastName')) {
        if (name) {
          name += ' ';
        }
        name += this.get('LastName');
      }
      return name;
    },
    parse: function (resp, options) { // jshint ignore:line
      var dotvaluededupe = {};

      // Trying to de-dupe multiple values like FirstName and FirstName.Value
      // without throwing out keys that don't have a .Value counterpart.
      _.each(resp, function (value, key, list) {
        key = databag_config.canonical[key] || key;
        if (!dotValue.regex.test(key)) {
          dotvaluededupe[key] = value;
        } else {
          // See if no key with this and .Value is in the response
          // If this is the only one, we want to ignore it.
          var newKey = key.replace(dotValue.regex, '');
          if (_.isUndefined(list[newKey])) {
            dotvaluededupe[newKey] = value;
          }
        }
      });

      return _.omit(dotvaluededupe, this.denylist('pull'));
    }
  });
});

